import React, { Component } from 'react'
import { BsSearch, BsArrowRepeat } from "react-icons/bs"
import Swal from 'sweetalert2'
import ModalOrders from './ModalComponent'
import LoadingWindow from './LoadingWindow'
import { getInformationWithDataV3 } from '../services/CABE'
import { getValueCookie } from '../services/cookieService'
import { intervalTimeInDays } from '../functions/generalFunctions'
import { ActualDate, DateFormatMAS90 } from '../functions/dateFormat'
import { isOnline, searchOfflineCustomers } from '../services/offlineCatalogCache'
import "../css/table-responsive.css"

/**
 * CustomerListV3 — customer picker for Sales Order V3. Rebuilt from scratch
 * (2026-08-19), replacing CustomerListV2.js for this one flow.
 *
 * CustomerListV2.js is a generic component shared across 6+ different pages
 * (Accounts, Customer, Promotions, Invoice, SalesOrder, plus a fallback
 * "customerManager" branch) via a big if/else on `nombrePadre`, all calling
 * v2 endpoints. This version only does the Sales Order path — same reasoning
 * CatalogueV3.js/SearchUPCV3.js were rebuilt lean instead of reusing the
 * shared v2 component (see their headers).
 *
 * One concrete bug this fixes by construction, not by patching v2:
 *   - CustomerListV2.js picks a customer by MUTATING Father.state.SalesOrder
 *     in place before calling setState — this made SalesOrderV3.js's own
 *     customer-change detection (in componentDidUpdate, comparing
 *     prevState vs state) never fire, so the "Price Level" badge only
 *     updated after a full page reload (found + worked around 2026-08-19,
 *     see SalesOrderV3.js's componentDidUpdate comment). This component
 *     builds a NEW SalesOrder object instead, so React's own state diffing
 *     works correctly without needing that workaround.
 *
 * The Balance popup is a DELIBERATE like-for-like port of v2's behavior, not
 * a fix: an earlier version filtered by the selected customer's CustomerNo
 * (more "correct"), but the user asked to match v2 instead (2026-08-19) —
 * mas90_mirror__AR_OpenInvoice only refreshes overnight, so for day-to-day
 * work they want the same lens v2 already uses rather than a stricter one
 * that can show $0 for a real customer whose invoices haven't synced yet.
 * Same as v2: filters by the CURRENT salesperson (SalespersonNo), not the
 * selected customer, and every row's CustomerName gets overwritten to the
 * selected customer's name — so the popup can show another customer's
 * invoice under the wrong name. Known v2 behavior, kept on purpose.
 *
 * Search/pagination is SERVER-SIDE (page/pageSize/total, same pattern as
 * CatalogueV3.js's getCatalog), not "fetch everything once and filter in the
 * browser" like CustomerListV2.js. An earlier version of this component did
 * fetch the whole `customer` table per company in one shot (matching v2's
 * own getListV2, which has no LIMIT either) — against a real production
 * table that came back as a ~1.3MB response taking close to the 30s axios/
 * SQL statement timeout, hanging the modal with no visible error (confirmed
 * 2026-08-19 via a Network tab capture). Also drops the offline/localStorage
 * customer caching layer, same documented trade-off as CatalogueV3.js — easy
 * to add back if field connectivity needs it. The payment-detail drill-down
 * (one invoice's payment history) still calls the v2 endpoint (a live
 * MAS90/WS call, no RDS mirror table exists for it yet) — everything else
 * here is RDS-only.
 */
export default class CustomerListV3 extends Component {
    ModalLoading = React.createRef()

    state = {
        customers: [],
        showModal: false,
        search: '',
        page: 1,
        pageSize: 50,
        total: 0,
        // OFFLINE-2026-09-29 — true only while actually serving the cached
        // customer list (see fetchCustomers()), so it's never ambiguous
        // whether the list shown is live or a saved copy.
        isOffline: false,
    }

    // Debounced auto-search (2026-09-01, client's ask — same treatment as
    // CatalogueV3.js's catalog search: waits 1s of no typing before searching
    // automatically, instead of requiring Enter or the button click). Enter
    // and the button still search immediately, clearing any pending timer so
    // a manual search never double-fires with the debounced one.
    searchDebounceTimer = null

    open = () => {
        this.setState({ showModal: true, search: '', page: 1 }, () => this.fetchCustomers())
    }

    close = () => {
        clearTimeout(this.searchDebounceTimer)
        this.setState({ showModal: false })
    }

    searchNow = () => {
        clearTimeout(this.searchDebounceTimer)
        this.setState({ page: 1 }, () => this.fetchCustomers())
    }

    // OFFLINE-2026-09-29 — serves the last-cached customer list (see
    // offlineCatalogCache.js's searchOfflineCustomers) when there's no
    // signal, or the live call fails/times out. Cache is warmed from
    // SalesOrderV3.js's componentDidMount, not by this component — see that
    // file's OFFLINE-2026-09-29 comment for why (this modal used to show
    // zero customers offline even after that fix elsewhere, because nothing
    // had ever populated the cache before this modal was the first one opened).
    loadOfflineCustomers(idCompany) {
        const { data, total } = searchOfflineCustomers(idCompany, getValueCookie('SalesPerson'), {
            search: this.state.search,
            page: this.state.page,
            pageSize: this.state.pageSize,
        })
        this.setState({ customers: data, total, isOffline: true })
    }

    async fetchCustomers() {
        this.ModalLoading.current.showState(true)
        try {
            const idCompany = getValueCookie('CompanyId')

            // OFFLINE-2026-09-29 — no signal: don't even attempt the live call.
            if (!isOnline()) {
                this.loadOfflineCustomers(idCompany)
                return
            }

            // Salesperson scoping (2026-09-01, client's ask) — idTypeofUser
            // 1/8/10 see every customer for the company; anyone else only
            // sees their own SalesPerson's customers. Both cookies are set
            // right after company selection (CompanyDashBoard.js), so they're
            // already available by the time this picker opens.
            const resp = await getInformationWithDataV3('/v3/salesorder/customer-list', {
                idCompany,
                search: this.state.search || undefined,
                page: this.state.page,
                pageSize: this.state.pageSize,
                salespersonNo: getValueCookie('SalesPerson'),
                idTypeofUser: getValueCookie('TypeUser'),
            })
            if (resp && resp.status && resp.status.code === 1) {
                this.setState({ customers: resp.data || [], total: resp.total || 0, isOffline: false })
            } else {
                this.setState({ customers: [], total: 0 })
            }
        } catch (error) {
            console.error('fetchCustomers failed:', error)
            // OFFLINE-2026-09-29 — network error mid-request (signal dropped
            // right as the request went out) — fall back to the cache.
            this.loadOfflineCustomers(getValueCookie('CompanyId'))
        } finally {
            this.ModalLoading.current.showState(false)
        }
    }

    onSearchChange = (e) => {
        const value = e.target.value
        this.setState({ search: value })
        clearTimeout(this.searchDebounceTimer)
        this.searchDebounceTimer = setTimeout(() => {
            this.setState({ page: 1 }, () => this.fetchCustomers())
        }, 1000)
    }

    onSearchKeyDown = (e) => {
        if (e.key === 'Enter') this.searchNow()
    }

    changePage(direction) {
        const { page, pageSize, total } = this.state
        const next = direction === 'next' ? page + 1 : Math.max(1, page - 1)
        if (direction === 'next' && (page * pageSize) >= total) return
        this.setState({ page: next }, () => this.fetchCustomers())
    }

    /**
     * Picks a customer for this Sales Order. Builds a brand new SalesOrder
     * object (never mutates Father.state.SalesOrder — see class header) and
     * stamps SalespersonNo from the CURRENT session user, same as
     * CustomerListV2.js did for this path — correct for a new SO, since the
     * logged-in salesperson IS the one making the sale.
     *
     * Re-pricing confirmation (2026-08-21, client's ask): switching customer
     * mid-order re-prices every cart line to the new customer's price level
     * (SalesOrderV3.js's componentDidUpdate -> loadPricingMap() ->
     * applyStandardPricing(), triggered once Father.state.SalesOrder.Customer
     * actually changes below). If there's already a customer AND at least one
     * visible product in the cart, confirm first — silently overwriting
     * prices the salesperson may have already negotiated would be a real
     * money mistake, not just a UI surprise. Declining aborts the customer
     * change entirely (nothing below runs), so the order never ends up with
     * a new customer but stale prices.
     */
    async selectCustomer(row) {
        const Father = this.props.Padre

        const hasExistingCustomer = !!(Father.state.SalesOrder.Customer && Father.state.SalesOrder.Customer.CustomerNo)
        const hasProducts = Array.isArray(Father.state.Products) && Father.state.Products.some((p) => p.Visible !== 0)
        if (hasExistingCustomer && hasProducts) {
            const result = await Swal.fire({
                title: 'Change customer?',
                text: "Changing the customer will recalculate all product prices to match the new customer's price level.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3aa0ff',
                cancelButtonColor: '#a8b3c7',
                confirmButtonText: 'Yes, continue',
                cancelButtonText: 'Cancel',
            })
            if (!result.isConfirmed) return
        }

        const priceLevel = row.priceLevel
        const warehouse = priceLevel === '4' ? '003' : priceLevel === '5' ? '004' : '000'

        const customer = {
            CustomerNo: row.customerNo,
            CustomerName: row.name,
            AddressLine1: row.addressLine1 && row.addressLine1 !== 'null' ? row.addressLine1 : '',
            AddressLine2: row.addressLine2 && row.addressLine2 !== 'null' ? row.addressLine2 : '',
            City: row.city || '',
            State: row.state || '',
            ZipCode: row.zipCode || '',
            TelephoneNo: row.phone1 || '',
            Email: row.email || '',
            TermsCode: row.termsCode || '',
            CreditLimit: row.creditLimit,
            PriceLevel: priceLevel,
            SalespersonNo: getValueCookie('SalesPerson'),
        }

        Father.setState({
            SalesOrder: { ...Father.state.SalesOrder, Customer: customer, Warehouse: warehouse },
        })

        // OFFLINE-2026-09-29 — the balance popup needs a live query
        // (mas90_mirror__AR_OpenInvoice has no offline copy — it's a
        // day-to-day informational lens, not needed to build the order
        // itself). Skip it without signal instead of hanging/erroring; the
        // customer is already picked above, so the salesperson can keep
        // working. It'll show normally next time this runs while online.
        if (!isOnline()) {
            this.close()
            return
        }

        this.ModalLoading.current.showState(true)
        try {
            const idCompany = getValueCookie('CompanyId')
            // Matches v2's CustomerListV2.js exactly: filtered by the CURRENT
            // salesperson, not the just-picked customer — see class header.
            const resp = await getInformationWithDataV3('/v3/salesorder/customer-balance', { idCompany, salespersonNo: getValueCookie('SalesPerson'), customerNo: row.customerNo })
            if (resp && resp.status && resp.status.code === 1) {
                const today = ActualDate()
                const formatInvoices = (rows) => (rows || []).map((r) => ({
                    ...r,
                    PaymentDelayDays: intervalTimeInDays(DateFormatMAS90(r.InvoiceDueDate), today),
                    InvoiceDate: DateFormatMAS90(r.InvoiceDate),
                    InvoiceDueDate: DateFormatMAS90(r.InvoiceDueDate),
                }))
                // Two lists now (2026-09-02, client's ask — "no tenemos una
                // lista de balances correctos por cliente"): customerInvoices
                // is the actually-correct one (filtered by THIS customer's own
                // CustomerNo); salespersonInvoices is the original
                // salesperson-wide list (informational, can include other
                // customers) — see salesOrderCustomer.ts's getBalance header
                // comment. Kept both, per the client's explicit ask, instead
                // of replacing one with the other.
                const customerInvoices = formatInvoices(resp.customerInvoices)
                const salespersonInvoices = formatInvoices(resp.salespersonInvoices)
                // customer.TermsCode comes from the `customer` table's own
                // termsCode column, which isn't always synced for every
                // customer (2026-08-19 — confirmed blank for a test account).
                // O.TermsCode from the balance query itself (per open invoice)
                // is the more reliably-populated source, so it's the fallback.
                const firstInvoice = (resp.customerInvoices && resp.customerInvoices[0]) || (resp.salespersonInvoices && resp.salespersonInvoices[0])
                const termsCode = customer.TermsCode || (firstInvoice && firstInvoice.TermsCode) || ''
                Father.setState({
                    // CustomerBalance (2026-08-31) is the reliable "does this
                    // customer owe money" number — AR_Customer.CurrentBalance
                    // for the SELECTED customer specifically.
                    Balance: {
                        CustomerInvoices: customerInvoices,
                        SalespersonInvoices: salespersonInvoices,
                        TermsCode: termsCode,
                        TotalBalance: 0,
                        CustomerBalance: resp.customerBalance || 0,
                        SelectedInvoice: '', Detail: [],
                    },
                }, () => Father.getTotalBalance())
                Father.handleModalOpen('showModal1')
            }
        } catch (error) {
            console.error('selectCustomer balance fetch failed:', error)
        } finally {
            this.ModalLoading.current.showState(false)
        }

        this.close()
    }

    getAddress(row) {
        const line2 = row.addressLine2 && row.addressLine2 !== 'null' ? row.addressLine2 : ''
        return row.addressLine1 && row.addressLine1 !== 'null' ? `${row.addressLine1} ${line2}`.trim() : ''
    }

    render() {
        const { showModal, search, customers, page, pageSize, total, isOffline } = this.state
        const totalPages = Math.max(1, Math.ceil(total / pageSize))

        return (
            <>
                <div className='col-sm-12 col-md-4'>
                    <button type="button" disabled={this.props.disabled} className="btn blueButton btn-lg w-100 shadow" onClick={this.open}>
                        <BsSearch /> Customer List
                    </button>
                </div>

                <LoadingWindow ref={this.ModalLoading} />

                {/* TABLEFONTBOOST-2026-10-01 — same portal issue as CatalogueV3.js's
                    modal (react-bootstrap's Modal renders to document.body, outside
                    SalesOrderV3.js's zoomed wrapper) — bodyZoom applies it directly here. */}
                <ModalOrders title={'Customer List'} show={showModal} close={this.close} bodyZoom={this.props.Padre.state.tableFontScale}>
                    <div className='row'>
                        {/* OFFLINE-2026-09-29 — only shown while actually serving the
                            cached copy, so it's never ambiguous whether this list is live. */}
                        {isOffline && (
                            <div className='row pt-1 d-flex justify-content-center'>
                                <div className='col-md-10'>
                                    <span style={{
                                        display: 'inline-flex', background: 'rgba(245,158,11,0.1)', color: '#92400E',
                                        border: '1px solid #FDE68A', borderRadius: '999px', padding: '4px 12px',
                                        fontSize: '0.78rem', fontWeight: 700,
                                    }}>Offline — showing saved customer list</span>
                                </div>
                            </div>
                        )}
                        <div className='row pt-1 pb-2'>
                            <div className='row d-flex justify-content-center pb-4'>
                                <div className='col-md-10 InputText'>
                                    <div className="card shadow">
                                        <div className="card-body d-flex justify-content-center">
                                            <div className="input-group input-group-lg flex-nowrap">
                                                <input className='form-control w-75 display-inline' placeholder='Search by CustomerNo or name (press Enter)'
                                                    value={search} onChange={this.onSearchChange} onKeyDown={this.onSearchKeyDown} />
                                                <button className='btn greenButton btn-lg' onClick={this.searchNow} style={{ height: '48px' }}><BsArrowRepeat /></button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='col-sm-12 tableFixHead'>
                            <table className='table table-striped align-middle table-hover'>
                                <thead>
                                    <tr>
                                        <th>Customer No</th>
                                        <th>Customer Name</th>
                                        <th>Address Line</th>
                                        <th>City</th>
                                        <th>Zip Code</th>
                                        <th>Telephone No</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* TABLEFONTBOOST-2026-10-01 — same unified base size as CatalogueV3.js's
                                        description and SalesOrderV3's .textTableSize (css/table-font-boost.css) —
                                        set once on the row, cascades to every <td>, client's ask: "todo deberia
                                        estar en el mismo tamaño". */}
                                    {customers.map((row, i) => (
                                        <tr key={row.customerNo || i} onClick={() => this.selectCustomer(row)} style={{ cursor: 'pointer', fontSize: 'calc(21px + 2pt)' }}>
                                            <td className='text-center'>{row.customerNo}</td>
                                            <td className='text-start'>{row.name}</td>
                                            <td className='text-start'>{this.getAddress(row)}</td>
                                            <td className='text-center'>{row.city}</td>
                                            <td className='text-center'>{row.zipCode}</td>
                                            <td className='text-center'>{row.phone1}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className='text-center'>
                                        <td>
                                            <div className="btn-group btn-group-lg" role="group" aria-label="pagination">
                                                <button disabled={page <= 1} type="button" onClick={() => this.changePage('prev')} className="btn blueButton">Prev</button>
                                            </div>
                                        </td>
                                        <td></td>
                                        <td className='text-center'>Page {page} of {totalPages}</td>
                                        <td></td>
                                        <td></td>
                                        <td className='text-center'>
                                            <div className="btn-group btn-group-lg" role="group" aria-label="pagination">
                                                <button disabled={page >= totalPages} type="button" onClick={() => this.changePage('next')} className="btn blueButton">Next</button>
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </ModalOrders>
            </>
        )
    }
}
